import {
  LAST_SUCCESS_TIME_LOG_IN,
  SLUG_LS,
  X_SCOPE_ID
} from "@/const/local-storage.constants"
import { RESOURCES_NAMES } from "@/const/resource.constants"

import type { User } from "@/domain/user/user"
import type { Services } from "@/interfaces/services"

type Settings = RequestInit & {
  params?: Record<string, string>
}

export type InitialParamsHttpAdapter = {
  auth: Services.IAuthAdapter
  logger: Services.ILoggerAdapter
  apiUrl?: string
  exchangeTokenUrl?: string
  localStorage?: Storage
}

type ResponseToken = {
  data: {
    features: Features.Response
    role: User["role"]
    fullName: User["fullName"]
    email?: string
    settings: Settings.Shared
  }
}

type OnReceiveFeatureFlags = (
  flags: Features.Response,
  role: User["role"],
  fullName: User["fullName"],
  settings: Settings.Shared
) => void

type PromiseWithResolve = {
  resolve?: () => void
  reject?: () => void
  promise: Promise<unknown>
}

export class HttpAdapter implements Services.IHttpAdapter {
  static COOKIE_EXPIRED_MS = 1000 * 60 * 15

  static SESSION_EXPIRED_MS = 1000 * 60 * 60 * 8

  static defaultInstance: Services.IHttpAdapter

  protected apiUrl

  protected auth: Services.IAuthAdapter

  protected logger: Services.ILoggerAdapter

  protected localStorage: Storage

  protected exchangeTokenUrl: string

  protected queue: null | Promise<unknown> = null

  protected waitingScopeIdPromise: null | PromiseWithResolve = null

  protected authWhiteList = [
    RESOURCES_NAMES.AUTHENTICATION.AUTH,
    RESOURCES_NAMES.TENANTS.SIGNUP,
    RESOURCES_NAMES.INTEGRATIONS.NOTIFY_CALLBACK,
    RESOURCES_NAMES.REPORTS.GENERATE,
    RESOURCES_NAMES.ASSETS_DB.VERSION,
    RESOURCES_NAMES.SCM_SIGN_IN.ROOT
  ]

  protected excludedFromWhiteList = [RESOURCES_NAMES.TENANTS.CONFIGURATIONS]

  protected excludedFromAuthWhiteList = [
    ...this.excludedFromWhiteList,
    RESOURCES_NAMES.AUTHENTICATION.CLI_CALLBACK,
    RESOURCES_NAMES.AUTHENTICATION.MCP_REQUEST,
    RESOURCES_NAMES.AUTHENTICATION.MCP_APPROVE,
    RESOURCES_NAMES.AUTHENTICATION.MCP_DENY,
    RESOURCES_NAMES.AUTHENTICATION.DOCS
  ]

  protected scopeIdWhiteList = [
    RESOURCES_NAMES.SETTINGS.SCOPES,
    RESOURCES_NAMES.ONBOARDING.ONBOARDING_STATUS,
    RESOURCES_NAMES.ONBOARDING.DISMISS_ONBOARDING,
    RESOURCES_NAMES.ONBOARDING.WELCOME_STATUS,
    RESOURCES_NAMES.ONBOARDING.DISMISS_WELCOME,
    ...this.authWhiteList
  ]

  protected headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json"
  }

  protected isAuthCookie: boolean = false

  protected featureFlags: null | Features.Response = null

  protected settings: Settings.Shared = { packagesExportLimits: 200000 }

  protected role: undefined | User["role"]

  protected fullName: undefined | User["fullName"]

  protected tokenExchangeTimestamp: null | number = null

  protected onReceiveFeatureFlags: OnReceiveFeatureFlags = () => {}

  protected signOut: () => void

  protected createHeadersSnapshot = () => {
    if (this.headers[X_SCOPE_ID])
      this.localStorage.setItem(X_SCOPE_ID, this.headers[X_SCOPE_ID])
    else this.localStorage.removeItem(X_SCOPE_ID)
  }

  protected restoreHeadersSnapshot = () => {
    const scopeId = this.localStorage.getItem(X_SCOPE_ID)

    if (scopeId) this.headers[X_SCOPE_ID] = scopeId
  }

  constructor(params: InitialParamsHttpAdapter) {
    const {
      apiUrl,
      exchangeTokenUrl,
      auth,
      logger,
      localStorage = window.localStorage
    } = params || {}

    this.apiUrl = apiUrl || ""
    this.exchangeTokenUrl = exchangeTokenUrl || ""
    this.auth = auth
    this.localStorage = localStorage
    this.logger = logger
    this.restoreHeadersSnapshot()
    this.signOut = async () => {
      await this.auth.signOut()
      window.location.reload()
    }
  }

  get isAuth() {
    return this.isAuthCookie
  }

  set isAuth(value) {
    this.isAuthCookie = value
  }

  private checkStatus(response: Response, endpoint = "") {
    try {
      if (response.ok) {
        return response
      }

      // An SCM sign-in token expiring is not a Kodem sign-out: the page signs in again itself.
      if (
        response.status === 401 &&
        !endpoint.startsWith(RESOURCES_NAMES.SCM_SIGN_IN.ROOT)
      ) {
        this.onAuthorizeError()
      }

      return Promise.reject(response)
    } catch {
      return Promise.reject(response)
    }
  }

  private parseEndpoint(
    endpoint: string,
    params: Record<string, string | string[]> = {}
  ) {
    const url = this.apiUrl + endpoint
    const searchParams = new URLSearchParams()

    Object.keys(params).forEach((key) => {
      const value = params[key]

      if (value === undefined || value === null) {
        return
      }

      if (Array.isArray(value)) {
        value.forEach((value) => {
          searchParams.append(key, value)
        })

        return
      }

      searchParams.append(key, value)
    })
    const querystring = searchParams.toString()

    return `${url}${querystring && "?"}${querystring}`
  }

  private parseSettings({
    body,
    method = "get",
    headers = {},
    ...otherSettings
  }: Settings) {
    const settings = {
      body: body ? JSON.stringify(body) : undefined,
      method,
      headers: { ...this.headers, ...headers },
      ...otherSettings
    }

    return settings
  }

  private onAuthorizeError() {
    return this.signOut()
  }

  private getPromiseWithResolver() {
    let resolve, reject
    const promise = new Promise((a, b) => {
      resolve = a
      reject = b
    })

    return { resolve, reject, promise }
  }

  private checkInWhiteList(
    whiteList: string[],
    endpoint: string,
    excludedList: string[] = this.excludedFromWhiteList
  ) {
    return (
      whiteList.some((value) => endpoint.includes(value)) &&
      !excludedList.some((exValue) => endpoint === exValue)
    )
  }

  private needRefreshCookie(endpoint: string) {
    const noRefreshCookie = this.checkInWhiteList(
      this.authWhiteList,
      endpoint,
      this.excludedFromAuthWhiteList
    )
    const isCookieExpired =
      this.tokenExchangeTimestamp &&
      Date.now() - this.tokenExchangeTimestamp > HttpAdapter.COOKIE_EXPIRED_MS

    if (noRefreshCookie) {
      return false
    }

    return !this.isAuth || isCookieExpired
  }

  private refreshCookie(endpoint: string, settings: Settings) {
    if (!this.queue) {
      this.queue = this.exchangeToken()
        .catch((err) => {
          this.onAuthorizeError()

          return Promise.reject(err)
        })
        .finally(() => {
          this.queue = null
        })
    }

    return this.queue.then(() => this.request(endpoint, settings))
  }

  private needScopeId(endpoint: string) {
    return (
      !this.headers[X_SCOPE_ID] &&
      !this.checkInWhiteList(this.scopeIdWhiteList, endpoint)
    )
  }

  private waitingScopeId() {
    if (!this.waitingScopeIdPromise)
      this.waitingScopeIdPromise = this.getPromiseWithResolver()

    return this.waitingScopeIdPromise.promise
  }

  private isSessionExpired() {
    try {
      const lastExchangeTime = this.localStorage.getItem(
        LAST_SUCCESS_TIME_LOG_IN
      )

      if (!lastExchangeTime) return true

      const lastExchangeTimeNumber = Number(lastExchangeTime)

      if (isNaN(lastExchangeTimeNumber)) return true

      const isExpired =
        Date.now() - lastExchangeTimeNumber > HttpAdapter.SESSION_EXPIRED_MS

      if (!isExpired) this.auth.setLastSuccessLoginTime()

      return isExpired
    } catch {
      return true
    }
  }

  private getContentType(response: Response) {
    try {
      return response.headers.get("Content-Type")?.toLowerCase() || ""
    } catch {
      return ""
    }
  }

  private request(endpoint: string, settings: Settings): Promise<any> {
    if (this.needRefreshCookie(endpoint)) {
      return this.refreshCookie(endpoint, settings)
    }

    if (this.needScopeId(endpoint)) {
      return this.waitingScopeId()
    }

    const { params, ...options } = settings

    return fetch(
      this.parseEndpoint(endpoint, params),
      this.parseSettings(options)
    )
      .then((res) => this.checkStatus(res, endpoint))
      .then(async (res) => {
        try {
          const { TRIAGE, KAI } = RESOURCES_NAMES

          if (res.status === 204) return
          if (endpoint === KAI.STREAM) return res

          const { ISSUES, ISSUES_APPLY, ISSUES_GENERATE } = TRIAGE

          const isGenerateFix =
            endpoint.startsWith(ISSUES) &&
            endpoint.includes(ISSUES_GENERATE) &&
            !endpoint.includes(`${ISSUES}/${ISSUES_GENERATE}`) && // endpoint for creating fix related Jira tickets
            !endpoint.endsWith(`/${ISSUES_APPLY}`) // endpoint for applying fix

          if (isGenerateFix) return res

          const contentType = this.getContentType(res)

          if (contentType.includes("text/csv")) return res

          const json = await res.json()

          return json
        } catch {
          return {}
        }
      })
      .catch(async (err) => {
        try {
          err.data = await err.json()

          if (err.status === 403) {
            if (err.data.detail === "auth/trial-days-expired") {
              return new Promise((_, reject) => {
                this.auth.trialDaysExpired(reject)
              })
                .then(() => err)
                .catch(() => Promise.reject(err))
            }
          }

          return Promise.reject(err)
        } catch {
          return Promise.reject(err)
        }
      })
  }

  private getTrialDays(slug: string) {
    const noTrialDays = -1

    try {
      const time = [84, 48, 48, 58, 48, 48, 58, 48, 48]
      const prefix = [50, 48, 50, 53, 45, 48]
      const all = {
        [String.fromCharCode(108, 105, 109, 101)]: {
          s: String.fromCharCode(...prefix, 52, 45, 50, 51, ...time),
          e: String.fromCharCode(...prefix, 53, 45, 48, 55, ...time)
        }
      }

      const dates = all[slug]

      if (!dates) return noTrialDays

      const currentDate = new Date()
      const startDate = new Date(dates.s)

      if (currentDate <= startDate) return noTrialDays

      const MS_PER_DAY = 1000 * 60 * 60 * 24
      const endDate = new Date(dates.e)
      const diff = Math.ceil(
        (endDate.getTime() - currentDate.getTime()) / MS_PER_DAY
      )

      return diff > 0 ? diff : noTrialDays
    } catch {
      return noTrialDays
    }
  }

  public getCurrentScopeId() {
    return this.headers[X_SCOPE_ID]
  }

  public removeHeader(name: string) {
    delete this.headers[name]
  }

  public reset() {
    this.removeHeader(X_SCOPE_ID)
    this.removeHeader("Authorization")
    this.localStorage.removeItem(X_SCOPE_ID)
  }

  public setCurrentScope(scopeId: string | null) {
    const fieldName = "X-Scope-Id"

    if (!scopeId) this.removeHeader(fieldName)
    else this.headers[fieldName] = scopeId

    this.createHeadersSnapshot()
    this.waitingScopeIdPromise?.resolve?.()
    this.waitingScopeIdPromise = null
  }

  public setFeatureFlags(onReceiveFeatureFlags: OnReceiveFeatureFlags) {
    this.onReceiveFeatureFlags = onReceiveFeatureFlags

    if (this.featureFlags && this.settings) {
      this.onReceiveFeatureFlags(
        this.featureFlags,
        this.role,
        this.fullName,
        this.settings
      )
    }
  }

  public setSignOut(signOut: () => void) {
    this.signOut = signOut
  }

  public async exchangeToken() {
    try {
      if (this.auth) {
        const firebaseToken = await this.auth.getToken()
        const slug = this.localStorage.getItem(SLUG_LS)
        const firebaseTokenExists = (firebaseToken ?? false) !== false
        // A Kodem SSO session is renewed by the API from its refresh cookie
        const isKodemSession = !!this.auth.isKodemSession?.()

        if (
          (!firebaseTokenExists && !isKodemSession) ||
          !slug ||
          this.isSessionExpired()
        ) {
          return Promise.reject()
        }

        const { data } = await this.post<ResponseToken>(
          this.exchangeTokenUrl,
          isKodemSession ? { slug } : { firebaseToken, slug }
        )

        if (!data) return Promise.reject()

        this.auth.syncUserWithAuthResponse(data?.email)

        const {
          features: featuresResponse,
          role,
          fullName = "",
          settings
        } = data
        const features = {
          [String.fromCharCode(116, 114, 105, 97, 108, 68, 97, 121, 115)]:
            this.getTrialDays(slug),
          ...featuresResponse
        }

        this.isAuth = true
        this.tokenExchangeTimestamp = Date.now()
        this.featureFlags = features || {}
        this.role = role
        this.settings = settings
        this.onReceiveFeatureFlags(
          this.featureFlags,
          role,
          fullName,
          this.settings
        )
      }
    } catch (err: any) {
      this.logger?.error(err, {
        status: err?.status,
        custom_event: "exchangeToken"
      })

      return Promise.reject()
    }
  }

  public get<T>(endpoint: string, settings: Settings): Promise<T> {
    return this.request(endpoint, { method: "get", ...settings })
  }

  public post<T>(
    endpoint: string,
    body: any,
    settings: Settings = {}
  ): Promise<T> {
    return this.request(endpoint, { method: "post", body, ...settings })
  }

  public put<T>(
    endpoint: string,
    body: any,
    settings: Settings = {}
  ): Promise<T> {
    return this.request(endpoint, { method: "put", body, ...settings })
  }

  public delete<T>(
    endpoint: string,
    settings: Settings = {},
    body?: any
  ): Promise<T> {
    if (body)
      return this.request(endpoint, { method: "delete", body, ...settings })

    return this.request(endpoint, { method: "delete", ...settings })
  }

  public patch<T>(
    endpoint: string,
    body: any,
    settings: Settings = {}
  ): Promise<T> {
    return this.request(endpoint, { method: "PATCH", body, ...settings })
  }
}
